/* eslint-disable max-lines-per-function */
import LocalStoreManager from "./local-store-manager";

import LoginProxy from "../proxies/back-end/authentication/login-proxy";
import GetCompanyUserProxy from "../proxies/back-end/company-user/get";
import vueStore from "../store";

let companyAdminPages = ["OnBoardingPage", "IncorporationPage"];

import { Constants } from "customer-sdk/constants";
const { SLEEK_APP_CACHE_KEY, SLEEK_DISABLE_CUSTOMER_APP } = Constants.STORE_CONSTANTS;
const { COMPANY_USER_MODULE, SET_COMPANY_USERS } = Constants.COMPANY_USERS_CONSTANTS;
const HTTP_STATUS_CODES = Constants.HTTP_STATUS_CODES;
const PAGE_URLS = Constants.PAGE_URLS;

let isUrlLocatedAt = (testUrl) => {
  return window.location.href.includes(testUrl);
};

let getCompanyUsers = async (id) => {
  let proxy = new GetCompanyUserProxy();
  let store = new LocalStoreManager();

  return await proxy.execute({ authToken: store.getToken(), id: id });
};

/**
 * @class AuthEnforcer
 * @description Module used for enforcing authenticated login in Sleek UI V2.
 */
export default class AuthEnforcer {
  /**
   * Try to authenticate if "sut" provided in URL
   * @returns {Promise<void>}
   */
  async reAuthenticatedIfSUTProvided() {
    let urlString = window.location.href;
    let url = new URL(urlString);
    let sut = url.searchParams.get("sut");
    let localStoreManager = new LocalStoreManager();
    if (sut !== null) {
      localStoreManager.logout();
      localStorage.setItem("isSUT", true);

      const proxy = new LoginProxy();
      try {
        const response = await proxy.loginWithSUT(sut);
        if (typeof response.auth_token !== "undefined") {
          const contextResponse = await proxy.getUserContext(response.auth_token);

          localStoreManager.storeProfile(null, contextResponse.data);

          //Redirect to the same page with SUT to prevent double authentication
          urlString = urlString.replace("sut=" + sut, "");

          window.location.href = urlString;
        } else {
          console.log("Failed to authenticate");
          window.location.href = "/";
          localStoreManager.logout();
        }
      } catch (e) {
        console.log(e);
        window.location.href = "/";
        localStoreManager.logout();
      }
    }
  }

  /**
   * @async
   * @function execute
   * @description Method used for enforcing authentication rules to viewing users.
   */
  /*eslint complexity: "off"*/
  async executeIfAuthenticated(action, component) {
    await this.reAuthenticatedIfSUTProvided();

    let localStoreManager = new LocalStoreManager();
    if (!localStoreManager.getToken()) {
      window.location.href = `/index.html?redirect=${window.location.pathname}${window.location.search}`;
      return;
    }

    if (localStoreManager.getIsTwoFactor()) {
      window.location.href = `/two-factor-auth/`;
      return;
    }

    let isEmailVerified = await localStoreManager.getItem("isEmailVerified");
    if (isEmailVerified === "false" && !(isUrlLocatedAt("/verify-account/") || isUrlLocatedAt("/verify/"))) {
      window.location.href = `/verify-account/index.html?redirect=${window.location.pathname}${window.location.search}`;
    }

    if (localStorage.getItem("isPartner") === "true") {
      if (PAGE_URLS.PARTNER.ACCESSIBLE_URLS.indexOf(window.location.pathname) < 0) {
        window.location.href = PAGE_URLS.PARTNER.INDEX;
      }
    }

    if (localStorage.getItem(SLEEK_DISABLE_CUSTOMER_APP) === "true") {
      if (
        window.location.pathname !== PAGE_URLS.INCORPORATION &&
        window.location.pathname !== PAGE_URLS.ONBOARDING &&
        window.location.pathname !== PAGE_URLS.HOME &&
        window.location.pathname !== PAGE_URLS.BILLING
      ) {
        console.log("from auth enforcer...");
        window.location.href = PAGE_URLS.HOME;
      }
    }

    let companyId = await localStoreManager.getCompanyId();
    if (
      companyId &&
      companyAdminPages.includes(component.name) &&
      !localStoreManager.getIsSleekAdmin() &&
      !localStoreManager.getIsAdmin()
    ) {
      let companyUsers = await getCompanyUsers(companyId);
      vueStore.dispatch(`${COMPANY_USER_MODULE}/${SET_COMPANY_USERS}`, companyUsers);

      const sleekAppCacheKey = (await localStoreManager.getItem(SLEEK_APP_CACHE_KEY)) || [];
      if (sleekAppCacheKey) {
        if (sleekAppCacheKey.length > 0) {
          let parsedSleekAppCache = JSON.parse(sleekAppCacheKey);
          if (
            parsedSleekAppCache &&
            parsedSleekAppCache.companyUsersModule &&
            parsedSleekAppCache.companyUsersModule.companyUsers
          ) {
            companyUsers = parsedSleekAppCache.companyUsersModule.companyUsers;
          }
        }
      } else {
        localStoreManager.logout();
      }
      let userId = await localStoreManager.getUserId();
      let companyOwner = companyUsers.filter((companyUser) => companyUser.user._id === userId && companyUser.is_owner);
      if (companyOwner && companyOwner.length > 0) {
        if (action instanceof Function) {
          action();
        }
      } else {
        window.location.href = PAGE_URLS.HOME;
      }
    } else {
      if (action instanceof Function) {
        action();
      }
    }
  }

  static isNonAuthPath() {
    const nonAuthPaths = ["post-payment-request"];
    const isNonAuth = !!(
      nonAuthPaths.find((path) => window.location.pathname.includes(path)) ||
      window.location.search.includes("oneTimeToken")
    );

    return isNonAuth;
  }

  static checkResponseIfAuthorized(responseStatus) {
    if (responseStatus === HTTP_STATUS_CODES.HTTP_UNAUTHORIZED && !this.isNonAuthPath()) {
      const localStoreManager = new LocalStoreManager();
      const authToken = localStoreManager.getToken();
      const isPreviouslyLoggedIn = !!authToken;

      localStoreManager.logout();

      const basePath = isPreviouslyLoggedIn ? PAGE_URLS.SESSION_EXPIRED : PAGE_URLS.LOGIN;
      const currentLocation = new URL(window.location.href);
      const existingRedirect = currentLocation.searchParams.get("redirect");
      const redirect = existingRedirect ? existingRedirect : currentLocation;

      window.location.href = `${basePath}?redirect=${encodeURIComponent(redirect)}`;
    }
  }
}
